import React, { useEffect, useState } from 'react'
import { isLoaded, isErrorState } from 'flux-entities'
import { Link } from 'react-router-dom'
import { Icons, SmartList, IHeaderItem, IColumnElement, Col, Row, Container } from '@doseme/cohesive-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons'

import { TProps } from './types'
import { HOSPITALS_URL, hospitalsEdit } from '../../../../constants/urls/frontend'
import { Modal } from '../../../../components/Modal/Modal'
import { ISubscription } from '../../../../types/subscription'
import { formatSubscriptionRows, splitCurrentAndExpiredSubscriptions } from '../utils'
import { alphabetical } from '../../../../utils/arrayUtils'
import { entitlementsForHospital } from '../../utils'
import { LoadingModal } from '../../../../components/LoadingModal'
import './index.scss'

const cols: IHeaderItem[] = [
  {
    name: 'Plan Name',
    className: 'col-9'
  },
  {
    name: 'Start Date',
  },
  {
    name: 'Expiry Date',
  },
]

const badge = (count: number) => {
  if (count === -1) {
    return (
      <div className='checkmark'>
        <FontAwesomeIcon
          className='fa-xs'
          data-testid='unlimited-checkmark'
          icon={faCheck}
        />
      </div>
    )
  }

  return (
    <div className='count'>
      {count}
    </div>
  )
}

const Show: React.FC<TProps> = (props) => {
  const [showExpiredPlans, setShowExpiredPlans] = useState(false)
  const {
    fetchHospitals,
    fetchPlans,
    fetchInstallations,
    fetchSubscriptions,
    hospitals,
    installations,
    subscriptions,
    plans,
    history,
  } = props

  useEffect(() => {
    fetchSubscriptions(props.match.params.id)
  }, [fetchSubscriptions, props.match.params.id])

  useEffect(() => {
    if (!hospitals.touched)     { fetchHospitals() }
  }, [hospitals.touched, fetchHospitals])

  useEffect(() => {
    if (!installations.touched) { fetchInstallations() }
  }, [installations.touched, fetchInstallations])

  useEffect(() => {
    if (!plans.touched)         { fetchPlans() }
  }, [plans.touched, fetchPlans])

  const redirect = () => {
    history.push(HOSPITALS_URL)
  }

  if (
    ![hospitals, installations, plans, subscriptions].every(isLoaded) ||
    ![hospitals.all, subscriptions.all].every((x) => x[props.match.params.id])
  ) {
    if (isLoaded(hospitals) && isErrorState(subscriptions)) {
      const error = subscriptions.errors[0].includes('404')
        ? `A hospital with id ${props.match.params.id} could not be found`
        : subscriptions.errors[0]

      return (
        <LoadingModal
          error={error}
          onHide={redirect}
        />
      )
    }

    return (
      <LoadingModal/>
    )
  }

  const columnContent = (sub: ISubscription): IColumnElement[] => [
    {
      name: 'planName',
      element: <span>{plans.all[sub.planId].type} - {plans.all[sub.planId].name}</span>,
      text: `${plans.all[sub.planId].type} - ${plans.all[sub.planId].name}`
    },
    {
      name: 'start',
      element: <span>{sub.start.format('MMM DD, YYYY')}</span>,
      text: sub.start.format('MMM DD, YYYY'),
    },
    {
      name: 'expiry',
      element: <span>{sub.end.format('MMM DD, YYYY')}</span>,
      text: sub.end.format('MMM DD, YYYY'),
    },
  ]

  const hospital = hospitals.all[props.match.params.id]

  const {
    currentSubscriptions,
    expiredSubscriptions
  } = splitCurrentAndExpiredSubscriptions(subscriptions.all[props.match.params.id])

  const allEntitlements = entitlementsForHospital(plans.all, subscriptions.all[props.match.params.id])
  const sortedEntitlements = allEntitlements.sort((a, b) => alphabetical(a.name, b.name))
  const planEntitlements = sortedEntitlements.length
    ? sortedEntitlements.map(drug =>
      <small key={drug.module} className='drug-item d-flex justify-content-between'>
        <div>{drug.name}</div>
        <div>{badge(drug.maxCourseCount)}</div>
      </small>
    )
    : <span>This hospital has no entitlements.</span>

  const modalHeader = (
    <section id='top'>
      <Container >
        <Row>
          <Col>
            <h3 className='co-h3'>{hospital.name}</h3>
            <h5 className='co-h5 hospital-instance'>{`ID: ${hospital.instanceId}`}</h5>
          </Col>
          <Col className='d-flex align-items-center justify-content-end'>
            <Link to={hospitalsEdit(hospital.id)}>
              <Icons.EditIconButton />
            </Link>
          </Col>
        </Row>
      </Container>
    </section>
  )

  const handleShowExpiredPlansToggled = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setShowExpiredPlans(evt.target.checked)
  }

  const modalBody = (
    <section id='middle'>
      <h4 className='co-h4'>Hospital Summary</h4>
      <div className='ui-card mb-5 mt-2'>
        <Container>
          <Row>
            <Col><strong>Instance:</strong></Col>
            <Col>{props.installations.all[hospital.installation]?.code || 'N/A'}</Col>
          </Row>
          <Row>
            <Col><strong>First User:</strong></Col>
            <Col>N/A</Col>
          </Row>
          <Row>
            <Col><strong>Active Users:</strong></Col>
            <Col>N/A</Col>
          </Row>
          <Row>
            <Col><strong>Timezone:</strong></Col>
            <Col>{hospital.timezone || 'N/A'}</Col>
          </Row>
        </Container>
      </div>
      <div className='mb-5'>
        <h4 className='co-h4 pb-2'>Hospital Subscriptions</h4>
        <Container>
          <SmartList
            cols={cols}
            data={formatSubscriptionRows(currentSubscriptions, columnContent)}
            textIfEmpty='This hospital has no active subscriptions.'
          />
          {showExpiredPlans && <SmartList
            header={false}
            cols={cols}
            data={formatSubscriptionRows(expiredSubscriptions, columnContent)}
          />}
        </Container>
        <div className='d-flex width-100 justify-content-end align-items-center mt-2'>
          <input
            id='show-expired-plans'
            type='checkbox'
            checked={showExpiredPlans}
            onChange={handleShowExpiredPlansToggled}
          />
          <label className='mb-0 ml-1' htmlFor='show-expired-plans'><small>Show expired plans</small></label>
        </div>
      </div>

      <h4 className='co-h4 pb-2'>Hospital Entitlements</h4>

      <div className='ui-card'>
        <Container>
          <Row>
            <Col>Max. Enabled Clinicians: <strong>{hospital.maxEnabledClinicians}</strong></Col>
          </Row>
        </Container>
        <hr />

        <Container>
          <div id='allowed'>
            <strong>Drug Entitlements Allowed (from plans):</strong>
          </div>

          <div id='drug-entitlements'>
            {planEntitlements}
          </div>
        </Container>
      </div>
    </section>
  )

  return (
    <>
      <Modal
        type='regular'
        show={true}
        onHide={redirect}
        header={modalHeader}
        body={modalBody}
      />
    </>
  )
}

export {
  Show
}
